import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Work as WorkIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  
  const [searchTerm, setSearchTerm] = useState('');

  const faqCategories = [
    {
      icon: <HelpIcon />,
      title: t('faq.categories.general'),
      color: '#28A0F0',
      count: 8,
    },
    {
      icon: <WorkIcon />,
      title: t('faq.categories.projects'),
      color: '#10b981',
      count: 12,
    },
    {
      icon: <PaymentIcon />,
      title: t('faq.categories.payments'),
      color: '#f59e0b',
      count: 6,
    },
    {
      icon: <SecurityIcon />,
      title: t('faq.categories.security'),
      color: '#ef4444',
      count: 5,
    },
    {
      icon: <AccountBalanceIcon />,
      title: t('faq.categories.legal'),
      color: '#8b5cf6',
      count: 4,
    },
    {
      icon: <SupportIcon />,
      title: t('faq.categories.support'),
      color: '#ec4899',
      count: 7,
    },
  ];

  const faqs = [
    {
      category: t('faq.categories.general'),
      question: t('faq.questions.whatIsWorkio'),
      answer: t('faq.answers.whatIsWorkio'),
    },
    {
      category: t('faq.categories.general'),
      question: t('faq.questions.howMuchCosts'),
      answer: t('faq.answers.howMuchCosts'),
    },
    {
      category: t('faq.categories.general'),
      question: t('faq.questions.howToRegister'),
      answer: t('faq.answers.howToRegister'),
    },
    {
      category: t('faq.categories.projects'),
      question: t('faq.questions.howToPostProject'),
      answer: t('faq.answers.howToPostProject'),
    },
    {
      category: t('faq.categories.projects'),
      question: t('faq.questions.howToApply'),
      answer: t('faq.answers.howToApply'),
    },
    {
      category: t('faq.categories.projects'),
      question: t('faq.questions.howSelectionWorks'),
      answer: t('faq.answers.howSelectionWorks'),
    },
    {
      category: t('faq.categories.payments'),
      question: t('faq.questions.howPaymentsWork'),
      answer: t('faq.answers.howPaymentsWork'),
    },
    {
      category: t('faq.categories.payments'),
      question: t('faq.questions.whenGetPaid'),
      answer: t('faq.answers.whenGetPaid'),
    },
    {
      category: t('faq.categories.payments'),
      question: t('faq.questions.paymentFees'),
      answer: t('faq.answers.paymentFees'),
    },
    {
      category: t('faq.categories.security'),
      question: t('faq.questions.howProtectData'),
      answer: t('faq.answers.howProtectData'),
    },
    {
      category: t('faq.categories.security'),
      question: t('faq.questions.canTrustFreelancers'),
      answer: t('faq.answers.canTrustFreelancers'),
    },
    {
      category: t('faq.categories.legal'),
      question: t('faq.questions.whatAreMyRights'),
      answer: t('faq.answers.whatAreMyRights'),
    },
    {
      category: t('faq.categories.legal'),
      question: t('faq.questions.whatHappensDisputes'),
      answer: t('faq.answers.whatHappensDisputes'),
    },
    {
      category: t('faq.categories.support'),
      question: t('faq.questions.howToContactSupport'),
      answer: t('faq.answers.howToContactSupport'),
    },
    {
      category: t('faq.categories.support'),
      question: t('faq.questions.whatToDoTechnicalProblem'),
      answer: t('faq.answers.whatToDoTechnicalProblem'),
    },
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryColor = (category: string) => {
    const cat = faqCategories.find(c => c.title === category);
    return cat ? cat.color : '#28A0F0';
  };

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="faq" />
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
{t('faq.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
{t('faq.subtitle')}
          </Typography>
        </Box>

        {/* Search Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <TextField
              fullWidth
                placeholder={t('faq.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#28A0F0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: '#ffffff',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: '#28A0F0' },
                  '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                },
                '& .MuiInputBase-input': { color: '#ffffff' },
              }}
            />
          </CardContent>
        </Card>


        {/* FAQ List */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
{searchTerm ? t('faq.searchResults', { term: searchTerm }) : t('faq.allQuestions')}
          </Typography>
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            {filteredFaqs.length === 0 ? (
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: '#a0a0a0',
                    mb: 2,
                    fontSize: { xs: '1rem', md: '1.25rem' },
                  }}
                >
{t('faq.noResults')}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#a0a0a0',
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                  }}
                >
{t('faq.noResultsDesc')}
                </Typography>
              </Card>
            ) : (
              filteredFaqs.map((faq, index) => (
                <Accordion
                  key={index}
                  sx={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: 2,
                    mb: 2,
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': {
                      margin: '0 0 16px 0',
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#28A0F0' }} />}
                    sx={{
                      '& .MuiAccordionSummary-content': {
                        margin: '16px 0',
                      },
                    }}
                  >
                    <Box>
                      <Chip
                        label={faq.category}
                        size="small"
                        sx={{
                          backgroundColor: `${getCategoryColor(faq.category)}20`,
                          color: getCategoryColor(faq.category),
                          border: `1px solid ${getCategoryColor(faq.category)}`,
                          mr: 2,
                          mb: 1,
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: { xs: '1rem', md: '1.25rem' },
                        }}
                      >
                        {faq.question}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#d0d0d0',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        lineHeight: 1.6,
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        </Box>

        {/* Contact CTA */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(40, 160, 240, 0.3)',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <HelpIcon sx={{ fontSize: 60, color: '#28A0F0', mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('faq.contactTitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
{t('faq.contactDesc')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontSize: { xs: '0.8rem', md: '0.875rem' },
                }}
              >
                support@workio.bg
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </Box>
  );
};

export default FAQPage;
